import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { ActionFailed, ConfirmDialogService, SaveForm, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { AutopilotAutomationBaseComponent } from '@tv3/containers/automations/automations-wizard/short-automations/autopilot-automation.component'
import { AmplitudeService } from '@tv3/services/amplitude.service'
import { select, Store } from '@ngrx/store'
import { concatMap, filter, map, tap, toArray } from 'rxjs/operators'
import { Actions, ofType } from '@ngrx/effects'
import {
  AutomationWizardCloseAction,
  AutomationWizardSaveAction,
} from '@tv3/containers/automations/automations-wizard/automations-wizard.actions'
import { UntypedFormGroup, Validators } from '@angular/forms'
import { AutomationHandler, AutomationTypes } from '@tokeet-frontend/automations'
import { combineLatest, of } from 'rxjs'
import {
  AddTemplateComplete,
  selectTemplatesForAutomationEvent,
  Template,
  TemplateService,
  TemplateType,
} from '@tokeet-frontend/templates'
import { predefinedReviewTemplates } from './reviews'
import { ChannelReviewSources } from '@tokeet-frontend/reviews'

@Component({
  selector: 'app-new-review-automation',
  templateUrl: './new-review-automation.component.html',
  styleUrls: ['./new-review-automation.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class NewReviewAutomationComponent extends AutopilotAutomationBaseComponent implements OnInit {
  @Input() type = AutomationTypes.Review
  @Input() handler = AutomationHandler.SendReview

  @Input() isWizard = true

  // predefined templates
  @Input('templates') predefinedTemplates: { subject: string; body: string; rating: number }[] =
    predefinedReviewTemplates

  channels = [ChannelReviewSources.Airbnb]

  templates: Template[] = []

  constructor(
    protected actions: Actions,
    protected templateService: TemplateService,
    protected store: Store<any>,
    protected amplitudeService: AmplitudeService,
    protected confirm: ConfirmDialogService
  ) {
    super(store, amplitudeService, confirm)
  }

  ngOnInit(): void {
    super.ngOnInit()
    this.form.get('hours')?.addValidators([Validators.min(4), Validators.max(23)])

    combineLatest([
      this.store.pipe(
        select(selectTemplatesForAutomationEvent(this.event)),
        map((items) =>
          items.filter((t) => t.type === TemplateType.Review && t.attributes?.autopilot === this.getAutopilotId())
        )
      ),
    ])
      .pipe(untilDestroy(this))
      .subscribe(([templates]) => {
        this.templates = templates
      })

    this.actions
      .pipe(
        ofType(AutomationWizardSaveAction),
        filter(() => this.viewType !== 'table' && this.isWizard),
        untilDestroy(this)
      )
      .subscribe((action) => {
        if (this.automationGroup) {
          this.doSave(this.form, action.closeAfterSave)
        } else if (action.closeAfterSave) {
          this.store.dispatch(AutomationWizardCloseAction())
        }
      })
  }

  tryToCreateTemplate() {
    if (this.templates.length > 0) {
      return of(this.templates)
    }

    const add = (subject: string, body: string, rating: number) => {
      return this.templateService
        .add(
          {
            name: this.name || this.label,
            description: '',
            subject,
            body,
            type: TemplateType.Review,
            event: this.event,
            attributes: {
              autopilot: this.getAutopilotId(),
            },
            rating,
          },
          { silent: true }
        )
        .pipe(tap((t) => this.store.dispatch(AddTemplateComplete({ template: t }))))
    }

    return of(...this.predefinedTemplates).pipe(
      concatMap((template) => add(template.subject, template.body, template.rating)),
      toArray()
    )
  }

  tryToCreateOrUpdateAutomation(status: boolean) {
    if (this.automationGroup) {
      this.toggleAutomation(status)
    } else {
      this.tryToCreateTemplate().subscribe(
        (templates) => {
          this.templates = templates
          this.doSave(this.form)
        },
        (error) => this.store.dispatch(ActionFailed({ error }))
      )
    }
  }

  @SaveForm()
  doSave(form: UntypedFormGroup, closeAfterSave = false) {
    this.saveAutomation({ ...this.form.getRawValue(), hours: 5, templates: this.templates.map((t) => t.id) })
    if (closeAfterSave) {
      this.store.dispatch(AutomationWizardCloseAction())
    }
  }

  getAutopilotId() {
    return `autopilot-review|${this.service}|${this.type}|${this.event}|${this.handler}|${this.idSuffix || ''}`
  }
}
